<template>
  <ZapiModalShell
    :visible="visible"
    title="Confirmar Venta"
    width="32rem"
    :loading="loading"
    cancelLabel="Cancelar"
    confirmLabel="Confirmar"
    @update:visible="$emit('update:visible', $event)"
    @confirm="manejarConfirmacion"
  >
    <div class="text-center py-4">      
      <h2 class="text-2xl font-bold mb-3">¿Desea procesar esta venta?</h2>
      <div class="surface-100 p-4 border-round mb-3">
        <p class="text-lg mb-2 text-600 font-bold">Total a Pagar</p>
        <p class="text-5xl font-bold zapi-text-red m-0 zapi-font">Bs. {{ total }}</p>
      </div>

      <!-- SECCIÓN DE PAGO MIXTO (Solo si el método principal no es Efectivo) -->
      <div v-if="metodoPrincipalId !== 1" class="text-left mt-4 border-top-1 surface-border pt-4">
        <div class="flex align-items-center justify-content-between mb-3">
          <label class="font-bold text-700">¿Pago Mixto (Efectivo + {{ nombreMetodoPrincipal }})?</label>
          <InputSwitch v-model="esPagoMixto" @change="resetearPagoMixto" />
        </div>

        <div v-if="esPagoMixto" class="grid formgrid p-fluid mt-2">
          <div class="field col-6">
            <label class="font-bold text-600">Efectivo (Bs)</label>
            <InputNumber 
              v-model="montoEfectivo" 
              mode="decimal" 
              :min="0" 
              :max="Number(total)" 
              :maxFractionDigits="2" 
              class="text-xl text-center" 
              @input="calcularRestante" 
            />
          </div>
          <div class="field col-6">
            <label class="font-bold text-600">{{ nombreMetodoPrincipal }} (Bs)</label>
            <div class="p-inputtext p-component bg-gray-100 flex align-items-center justify-content-center text-xl font-bold text-blue-600">
              {{ montoRestante }}
            </div>
          </div>
        </div>
      </div>
      
      <p v-if="!esPagoMixto" class="text-500 text-lg m-0 font-bold mt-2">Verifique el monto antes de confirmar la venta.</p>
    </div>
  </ZapiModalShell>
</template>

<script>
import ZapiModalShell from '@/components/shared/ZapiModalShell.vue';
import InputSwitch from 'primevue/inputswitch';
import InputNumber from 'primevue/inputnumber';

export default {
  name: 'ZapiModalConfirmacionVenta',
  components: {
    ZapiModalShell,
    InputSwitch,
    InputNumber
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    total: {
      type: [Number, String],
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    metodoPrincipalId: {
      type: Number,
      default: 1 // 1 = Efectivo, 2 = Tarjeta, 6 = QR
    },
    nombreMetodoPrincipal: {
      type: String,
      default: 'Digital'
    }
  },
  emits: ['update:visible', 'confirm'],
  data() {
    return {
      esPagoMixto: false,
      montoEfectivo: 0
    }
  },
  computed: {
    /**
     * Calcula el monto restante que se pagará con el método digital seleccionado.
     * Resta el monto pagado en efectivo del total de la venta.
     * 
     * @returns {string} Monto restante formateado a 2 decimales.
     */
    montoRestante() {
      const efec = Number(this.montoEfectivo) || 0;
      const tot = Number(this.total) || 0;
      const restante = tot - efec;
      return restante > 0 ? restante.toFixed(2) : '0.00';
    }
  },
  watch: {
    // Al abrir el modal, reseteamos el estado del pago mixto por seguridad
    visible(newVal) {
      if (newVal) {
        this.esPagoMixto = false;
        this.montoEfectivo = 0;
      }
    }
  },
  methods: {
    /**
     * Reinicia el monto de efectivo a 0 si el usuario apaga el interruptor de pago mixto.
     */
    resetearPagoMixto() {
      if (!this.esPagoMixto) {
        this.montoEfectivo = 0;
      }
    },
    /**
     * Asegura de forma reactiva que el cajero no pueda introducir un monto en efectivo
     * mayor al total de la cuenta.
     * 
     * @param {object} event - Evento nativo de PrimeVue InputNumber
     */
    calcularRestante(event) {
      if (event.value > Number(this.total)) {
        this.montoEfectivo = Number(this.total);
      }
    },
    /**
     * Consolida los montos ingresados y emite el evento de confirmación
     * hacia el componente principal (VentaPOS.vue) con el payload de montos listos.
     */
    manejarConfirmacion() {
      const totalNum = Number(this.total) || 0;
      let efectivoFinal = 0;
      let digitalFinal = 0;

      if (this.metodoPrincipalId === 1) {
        // Todo en efectivo (Método principal es Efectivo)
        efectivoFinal = totalNum;
      } else {
        if (this.esPagoMixto) {
          efectivoFinal = Number(this.montoEfectivo) || 0;
          digitalFinal = totalNum - efectivoFinal;
        } else {
          // Todo digital (QR o Tarjeta sin división)
          digitalFinal = totalNum;
        }
      }

      this.$emit('confirm', {
        efectivo: efectivoFinal,
        monto_pago: digitalFinal
      });
    }
  }
};
</script>

<style scoped>
.zapi-text-red {
  color: #e32f3a;
}
.zapi-font {
  font-family: 'Impact', 'Arial Black', sans-serif;
  letter-spacing: 1px;
}
</style>
